/**
 * How a loan tape column was made, in the three words the page uses for it
 * (DOP-627): the standard loan-tape vocabulary, Mapped / Derived / Unmapped.
 *
 * The run's preview block carries one lineage record per column, and the
 * backend already decides two things about each: whether the column is a
 * straight copy (`kind: "extracted"`) and which raw tab/column it reads
 * (`sources`). Those two facts are the whole classification:
 *
 * - **Mapped**: taken straight from a column of the new file.
 * - **Derived**: calculated, from its columns or from the tape's own
 *   settings. A derived column with no source column is one of those (the
 *   cutoff date, or a due date counted from it), not a gap.
 * - **Unmapped**: the run has no lineage for it, or says it is a straight copy
 *   and cannot name the column it copied. A person should look before the
 *   tape goes out. This is the only "unresolved" signal a run's blocks carry
 *   today.
 */

import type {
	ContentBlock,
	LoanTapePreviewColumn,
	ModifiedWorkbookBlock,
} from "../Chat/Chat.types";

export type ColumnOrigin = "mapped" | "derived" | "unmapped";

export const columnOrigin = (column: LoanTapePreviewColumn): ColumnOrigin => {
	const lineage = column.lineage;
	if (!lineage) return "unmapped";
	if (lineage.kind === "derived") return "derived";
	return lineage.sources.length > 0 ? "mapped" : "unmapped";
};

export const ORIGIN_LABEL: Record<ColumnOrigin, string> = {
	mapped: "Mapped",
	derived: "Derived",
	unmapped: "Unmapped",
};

/** The dot beside a label. */
export const ORIGIN_DOT: Record<ColumnOrigin, string> = {
	mapped: "bg-[#008782]",
	derived: "bg-[#444ce7]",
	unmapped: "bg-[#dc6803]",
};

/** The label's own colour: darker than the dot, so it reads as text. */
export const ORIGIN_TEXT: Record<ColumnOrigin, string> = {
	mapped: "text-[#004C4B]",
	derived: "text-[#3538cd]",
	unmapped: "text-[#b54708]",
};

/** The pill on the column panel. */
export const ORIGIN_PILL: Record<ColumnOrigin, string> = {
	mapped: "bg-[#E6F3F3] text-[#004C4B]",
	derived: "bg-[#eef4ff] text-[#3538cd]",
	unmapped: "bg-[#fffaeb] text-[#b54708]",
};

export const ORIGINS: readonly ColumnOrigin[] = ["mapped", "derived", "unmapped"];

export type OriginCounts = Record<ColumnOrigin, number>;

export const countOrigins = (
	columns: readonly LoanTapePreviewColumn[],
): OriginCounts => {
	const counts: OriginCounts = { mapped: 0, derived: 0, unmapped: 0 };
	columns.forEach((column) => {
		counts[columnOrigin(column)] += 1;
	});
	return counts;
};

/** The columns a person should look at before the tape goes out. */
export const unmappedColumns = (
	columns: readonly LoanTapePreviewColumn[],
): LoanTapePreviewColumn[] =>
	columns.filter((column) => columnOrigin(column) === "unmapped");

/**
 * The summary line the extractor writes above the preview:
 * "converted into a loan tape → **100 loans, 33 columns**".
 */
const LOANS_IN_SUMMARY = /\*\*([\d,]+) loans, \d+ columns\*\*/;

/**
 * How many loans the whole tape has, when the run says.
 *
 * The preview block holds the first rows only. The workbook card's preview,
 * when it has one, counts the written sheet's rows, header included;
 * otherwise the run's summary line states the count. `null` when neither
 * does: a guess would pass the 20-row sample off as the tape's size.
 */
export const loanCountOf = (
	workbook: ModifiedWorkbookBlock | null,
	blocks: readonly ContentBlock[] = [],
): number | null => {
	const rows = workbook?.preview?.row_count;
	if (typeof rows === "number" && rows >= 1) return rows - 1;
	for (const block of blocks) {
		if (block.type !== "text") continue;
		const match = LOANS_IN_SUMMARY.exec(block.text);
		if (match) return Number(match[1].replace(/,/g, ""));
	}
	return null;
};

/** `Loans › Balance`: where a source column sits in the new file. */
export const sourceLabel = (source: { tab: string; column: string }): string =>
	source.tab ? `${source.tab} › ${source.column}` : source.column;
